import { useCallback } from 'react';
import { supabase, Database } from './supabase';
import { uploadImageToStorage, getPublicUrl } from './storageHelpers';
import { v4 as uuid } from 'uuid';

export type GameArt = Database['public']['Tables']['game_art']['Row'];

export type AssetSource = 'code_placeholder' | 'user_upload' | 'selected_art' | 'ai_generated';

// Constants for storage
const STORAGE_BUCKET = 'playmix-assets';
const IMAGE_FOLDER = 'game-art';

export const useGameArt = () => {
  
  const subscribeToGameArt = useCallback((gameId: string, onChange: (row: any, type: 'INSERT'|'UPDATE'|'DELETE') => void) => {
    if (!gameId) return () => {};
    const channel = supabase
      .channel(`game-art:${gameId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'game_art', filter: `game_id=eq.${gameId}` },
        payload => {
          const eventType = (payload as any).eventType as 'INSERT'|'UPDATE'|'DELETE';
          const rawNew = (payload as any).new;
          const rawOld = (payload as any).old;

          if (eventType === 'DELETE') {
            onChange(rawOld, 'DELETE');
            return;
          }

          const normalized = normalizeAndFilterRow((rawNew || rawOld) as any);
          if (!normalized) {
            // If row becomes non-visible after update/insert, surface as DELETE to callers
            const basis = rawNew || rawOld;
            onChange(basis, 'DELETE');
            return;
          }
          onChange(normalized, eventType);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Normalize URL and apply visibility rules consistently
  const normalizeAndFilterRow = useCallback((row: GameArt | null): GameArt | null => {
    if (!row) return null;
    const normalized: GameArt = { ...row } as GameArt;
    try {
      normalized.asset_url = getPublicUrl(supabase, normalized.asset_url);
    } catch (_) {}
    const source = String((normalized as any)?.asset_source || '');
    const isStale = Boolean((normalized as any)?.is_description_stale);
    const hasUrl = Boolean(normalized && normalized.asset_url);

    if (!hasUrl) {
      return source === 'ai_generated' && isStale === true ? normalized : null;
    }
    if (source === 'ai_generated') {
      return isStale === false ? normalized : null;
    }
    return normalized;
  }, []);

  /**
   * Creates a new game art entry in the database or updates an existing one
   * @param gameId - The ID of the game
   * @param name - The name of the art
   * @param asset - The art image as a string (base64 or URL)
   * @param assetSource - The source of the asset (code_placeholder | user_upload | selected_art | ai_generated)
   * @param artId - The ID of the art (optional)
   * @param id - The ID of the game art to update (optional)
   * @returns The created or updated GameArt object
   */
  const associateGameArt = useCallback(async (
    gameId: string,
    name: string,
    asset: string,
    assetSource: AssetSource,
    artId?: string,
    id?: string,
  ): Promise<GameArt> => {
    // console.log('Associating game art with name:', name, 'id:', id);
    
    // Upload the image to storage directly
    const uniqueId = uuid();
    const storagePath = await uploadImageToStorage(
      supabase, 
      asset, 
      `game-${gameId}-art-${artId || uniqueId}`, 
      IMAGE_FOLDER
    );
    // console.log('Uploaded image to storage at storagePath:', storagePath);
    
    // Convert storage path to public URL
    const publicUrl = getPublicUrl(supabase, storagePath);
    // console.log('Generated public URL:', publicUrl);

    let existingArt = null;
    let existingArtError = null;
    
    // If id is provided, use it to find the existing entry
    if (id) {
      // console.log('Finding existing game art by id:', id);
      const { data, error } = await supabase
        .from('game_art')
        .select('*')
        .eq('id', id)
        .single();
      
      existingArt = data;
      existingArtError = error;
    } 
    // Otherwise check if there's an existing entry with the same game_id and art_id
    else if (artId) {
      // console.log('Finding existing game art by game_id and art_id:', gameId, artId);
      const { data, error } = await supabase
        .from('game_art')
        .select('*')
        .eq('game_id', gameId)
        .eq('art_id', artId)
        .single();
      
      existingArt = data;
      existingArtError = error;
    }

    if (existingArtError && existingArtError.code !== 'PGRST116') {
      console.error('Error checking existing game art:', existingArtError);
      throw existingArtError;
    }

    let gameArt;
    let gameArtError;

    if (existingArt) {
      // Update existing entry
      // console.log('Updating existing game art entry with id:', existingArt.id);
      const isStale = assetSource === 'user_upload' || assetSource === 'selected_art';
      const { data: updatedArt, error: updateError } = await supabase
        .from('game_art')
        .update({
          asset_url: publicUrl,
          asset_name: name,
          art_id: artId || null,
          asset_source: assetSource,
          is_description_stale: isStale,
        })
        .eq('id', existingArt.id) // Add the filter condition to specify which record to update
        .select('*')
        .single();

      gameArt = updatedArt;
      gameArtError = updateError;
    } else {
      // Create new entry
      // console.log('Creating new game art entry');
      const isStale = assetSource === 'user_upload' || assetSource === 'selected_art';
      const { data: newArt, error: insertError } = await supabase
        .from('game_art')
        .insert({
          game_id: gameId,
          art_id: artId || null,
          asset_url: publicUrl,
          asset_name: name,
          asset_source: assetSource,
          is_description_stale: isStale,
        })
        .select('*')
        .single();

      gameArt = newArt;
      gameArtError = insertError;
    }
    
    if (gameArtError) {
      console.error('Error associating game art:', gameArtError);
      throw gameArtError;
    }
    
    if (!gameArt) {
      console.error('No game art returned after operation');
      throw new Error('No game art returned after operation');
    }
    
    // console.log('Successfully associated game art:', gameArt);
    return gameArt;
  }, []);

  /**
   * Lists all art associated with a specific game or art
   * @param gameId - The ID of the game (optional if artId is provided)
   * @param artId - The ID of the art (optional if gameId is provided)
   * @returns Array of GameArt objects
   */
  const listGameArt = useCallback(async (
    gameId?: string,
    artId?: string
  ): Promise<GameArt[]> => {
    // Validate that at least one ID is provided
    if (!gameId && !artId) {
      console.error('Error listing game art: Either gameId or artId must be provided');
      throw new Error('Either gameId or artId must be provided');
    }

    // Start building the query
    let query = supabase
      .from('game_art')
      .select('*')
      // .order('updated_at', { ascending: false });
      .order('created_at', { ascending: true });
      // .order('asset_name', { ascending: true });
    
    // Apply filters based on which ID is provided
    if (gameId) {
      query = query.eq('game_id', gameId);
    }
    
    if (artId) {
      query = query.eq('art_id', artId);
    }
    
    // Execute the query
    const { data: gameArts, error: gameArtsError } = await query;

    if (gameArtsError) {
      console.error('Error listing game art:', gameArtsError);
      throw gameArtsError;
    }

    // Normalize and apply visibility rules
    const rows = (gameArts || []) as GameArt[];
    const filtered = rows
      .map(r => normalizeAndFilterRow(r))
      .filter((r): r is GameArt => Boolean(r));

    return filtered;
  }, []);

  /**
   * Fetch a single game_art row by its id and apply the same filtering rules as listGameArt.
   */
  const getGameArtById = useCallback(async (
    id: string
  ): Promise<GameArt | null> => {
    if (!id) return null;
    const { data, error } = await supabase
      .from('game_art')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if ((error as any).code === 'PGRST116') return null;
      console.error('Error fetching game_art by id:', error);
      throw error;
    }
    if (!data) return null;
    return normalizeAndFilterRow(data as GameArt);
  }, []);

  /**
   * Fetch a single game_art row by (game_id, asset_name) and apply the same filtering rules as listGameArt.
   */
  const getGameArtByName = useCallback(async (
    gameId: string,
    assetName: string
  ): Promise<GameArt | null> => {
    if (!gameId || !assetName) return null;
    const { data, error } = await supabase
      .from('game_art')
      .select('*')
      .eq('game_id', gameId)
      .eq('asset_name', assetName)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) {
      if ((error as any).code === 'PGRST116') return null;
      console.error('Error fetching game_art by name:', error);
      throw error;
    }
    if (!data) return null;
    return normalizeAndFilterRow(data as GameArt);
  }, []);
  
  /**
   * Restores the live game_art rows for a game to exactly match the provided snapshot.
   * Non-atomic client-side operation: performs updates/inserts, then deletes extras.
   */
  const restoreGameArtToSnapshot = useCallback(async (
    gameId: string,
    snapshot: Array<Partial<GameArt>>
  ): Promise<void> => {
    if (!gameId || !Array.isArray(snapshot)) return;

    const { data: liveRows, error: liveErr } = await supabase
      .from('game_art')
      .select('*')
      .eq('game_id', gameId);
    if (liveErr) {
      console.error('Error reading live game_art for restore:', liveErr);
      throw liveErr;
    }

    const live = (liveRows || []) as GameArt[];

    const liveById = new Map<string, GameArt>();
    for (const row of live) {
      if (row.id) liveById.set(row.id, row);
    }

    // Updates/Inserts
    for (const s of snapshot) {
      if (!s) continue;
      const snapId = (s as any).id as string | undefined;
      const snapArtId = (s as any).art_id as string | null | undefined;
      const snapAssetName = (s as any).asset_name as string | undefined;
      const snapAssetUrl = (s as any).asset_url as string | undefined;
      const snapAssetSource = (s as any).asset_source as string | null | undefined;

      if (!snapAssetName || !snapAssetUrl) continue;

      let match: GameArt | undefined = undefined;
      if (snapId) {
        match = liveById.get(snapId);
      }
      if (!match && snapArtId) {
        match = live.find(a => a.art_id && a.art_id === snapArtId) as GameArt | undefined;
      }
      if (!match && !snapArtId && snapAssetName) {
        match = live.find(a => !a.art_id && a.asset_name === snapAssetName) as GameArt | undefined;
      }

      if (match) {
        const { error: upErr } = await supabase
          .from('game_art')
          .update({
            art_id: snapArtId || null,
            asset_url: snapAssetUrl,
            asset_name: snapAssetName,
            asset_source: (snapAssetSource as any) ?? null,
            is_description_stale: false,
          })
          .eq('id', match.id);
        if (upErr) {
          console.error('Error updating game_art during restore:', upErr);
          throw upErr;
        }
      } else {
        const { error: insErr } = await supabase
          .from('game_art')
          .insert({
            game_id: gameId,
            art_id: snapArtId || null,
            asset_url: snapAssetUrl,
            asset_name: snapAssetName,
            asset_source: (snapAssetSource as any) ?? null,
            is_description_stale: false,
          });
        if (insErr) {
          console.error('Error inserting game_art during restore:', insErr);
          throw insErr;
        }
      }
    }

    // Deletes for rows not present in snapshot
    const snapshotById = new Map<string, any>();
    for (const s of snapshot) {
      const sid = (s as any).id as string | undefined;
      if (sid) snapshotById.set(sid, s);
    }
    for (const a of live) {
      const inSnapById = a.id && snapshotById.has(a.id);
      const inSnapByArt = !inSnapById && a.art_id ? snapshot.some(s => (s as any).art_id && (s as any).art_id === a.art_id) : false;
      const inSnapByName = !inSnapById && !a.art_id ? snapshot.some(s => !(s as any).art_id && (s as any).asset_name === a.asset_name) : false;
      const inSnapshot = Boolean(inSnapById || inSnapByArt || inSnapByName);
      if (!inSnapshot) {
        const { error: delErr } = await supabase
          .from('game_art')
          .delete()
          .eq('id', a.id);
        if (delErr) {
          console.error('Error deleting game_art during restore:', delErr);
          throw delErr;
        }
      }
    }
  }, []);
  
  return {
    associateGameArt,
    listGameArt,
    getGameArtById,
    getGameArtByName,
    subscribeToGameArt,
    restoreGameArtToSnapshot,
  };
};
